import { useState } from "react";
import { useIsAdmin } from "@/lib/session";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import type { CustomField } from "@/lib/types";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { useCustomFields, useFieldGroups } from "../contacts/hooks";

const TYPES: { value: CustomField["type"]; label: string }[] = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Whole number" },
  { value: "decimal", label: "Number with decimals" },
  { value: "money", label: "Money" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date and time" },
  { value: "boolean", label: "Yes / no" },
  { value: "select", label: "Pick one" },
  { value: "multiselect", label: "Pick several" },
  { value: "url", label: "Web address" },
  { value: "email", label: "Email address" },
  { value: "phone", label: "Phone number" },
];

/** Custom fields and their groups (the contact screen shows them in these groups). */
export function FieldsPanel() {
  const isAdmin = useIsAdmin();
  const fields = useCustomFields(true);
  const groups = useFieldGroups();
  const [editing, setEditing] = useState<CustomField | "new" | null>(null);
  const [newGroup, setNewGroup] = useState(false);
  const rows = fields.data?.fields ?? [];
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>Custom fields</CardTitle>
        {isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setNewGroup(true)}>
              New group
            </Button>
            <Button size="sm" onClick={() => setEditing("new")}>
              <Plus /> New field
            </Button>
          </div>
        )}
      </CardHeader>
      {!isAdmin && <p className="px-4 pt-2 text-xs text-[var(--color-warning-fg)]">Only an admin can change these. You can look.</p>}
      <p className="px-4 py-2 text-sm text-[var(--color-muted-foreground)]">
        Extra facts about a person (parks owned, investor stage…). A field's key and type never change once made, because values are stored under the key.
        Fields marked “personal data” are kept out of logs.
      </p>
      {fields.isLoading && <Skeleton className="m-3 h-40" />}
      {!fields.isLoading && rows.length === 0 && <Empty title="No custom fields yet." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Label</TH>
              <TH>Key</TH>
              <TH>Type</TH>
              <TH>Group</TH>
              <TH>Choices</TH>
              <TH>Order</TH>
              <TH />
            </tr>
          </THead>
          <tbody>
            {rows.map((f) => (
              <TR key={f.id} className={f.archived_at ? "row-dim" : ""}>
                <TD className="font-medium">
                  {f.label}
                  {f.pii && (
                    <Badge variant="warning" className="ml-1.5">
                      personal data
                    </Badge>
                  )}
                  {f.archived_at && (
                    <Badge variant="muted" className="ml-1.5">
                      archived
                    </Badge>
                  )}
                </TD>
                <TD className="num text-xs">{f.key}</TD>
                <TD>{TYPES.find((t) => t.value === f.type)?.label ?? f.type}</TD>
                <TD>{f.group_name ?? "—"}</TD>
                <TD className="max-w-[16rem] truncate text-xs" title={f.options.map((o) => o.label).join(", ")}>
                  {f.options.map((o) => o.label).join(", ") || "—"}
                </TD>
                <TD className="num">{f.sort}</TD>
                <TD className="text-right">
                  {isAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => setEditing(f)}>
                      Edit
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      {groups.data && groups.data.groups.length > 0 && (
        <p className="border-t px-4 py-2 text-xs text-[var(--color-muted-foreground)]">Groups: {groups.data.groups.map((g) => g.name).join(", ")}</p>
      )}
      {editing && <FieldDialog field={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      {newGroup && <GroupDialog onClose={() => setNewGroup(false)} />}
    </Card>
  );
}

function FieldDialog({ field, onClose }: { field: CustomField | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const groups = useFieldGroups();
  const [label, setLabel] = useState(field?.label ?? "");
  const [key, setKey] = useState(field?.key ?? "");
  const [type, setType] = useState<CustomField["type"]>(field?.type ?? "text");
  const [group, setGroup] = useState<number | "">(field?.group_id ?? "");
  const [options, setOptions] = useState((field?.options ?? []).map((o) => o.value).join("\n"));
  const [pii, setPii] = useState(field?.pii ?? false);
  const [sort, setSort] = useState(String(field?.sort ?? 0));
  const [archived, setArchived] = useState(!!field?.archived_at);
  const autoKey = (l: string) =>
    l
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^(\d)/, "f_$1")
      .slice(0, 63);
  const opts = options
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  const save = useMutation({
    mutationFn: () =>
      field
        ? api(`/api/v1/custom-fields/${field.id}`, {
            method: "PATCH",
            json: { label, group_id: group === "" ? 0 : group, sort: Number(sort) || 0, pii, archived, options: type === "select" || type === "multiselect" ? opts : undefined },
          })
        : api("/api/v1/custom-fields", { json: { key, label, type, group_id: group || undefined, options: opts.length ? opts : undefined, pii, sort: Number(sort) || 0 } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["custom-fields"] });
      toast(field ? "Field saved" : `Field "${label}" created`);
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{field ? `Edit “${field.label}”` : "New custom field"}</DialogTitle>
        <DialogDescription>{field ? `Key ${field.key}, type ${TYPES.find((t) => t.value === field.type)?.label}: these stay as they are.` : "Pick the type carefully: it can't change later."}</DialogDescription>
        <form
          className="grid grid-cols-2 gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="col-span-2 space-y-1">
            <Label htmlFor="cf-label">Label</Label>
            <Input
              id="cf-label"
              autoFocus
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                if (!field) setKey(autoKey(e.target.value));
              }}
              required
            />
          </div>
          {!field && (
            <>
              <div className="space-y-1">
                <Label htmlFor="cf-key">Key (letters, numbers, _)</Label>
                <Input id="cf-key" value={key} onChange={(e) => setKey(e.target.value)} pattern="[a-z][a-z0-9_]{0,62}" required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="cf-type">Type</Label>
                <Select id="cf-type" value={type} onChange={(e) => setType(e.target.value as CustomField["type"])}>
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </div>
            </>
          )}
          <div className="space-y-1">
            <Label htmlFor="cf-group">Group</Label>
            <Select id="cf-group" value={group} onChange={(e) => setGroup(e.target.value ? Number(e.target.value) : "")}>
              <option value="">None (Other fields)</option>
              {(groups.data?.groups ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cf-sort">Order in its group</Label>
            <Input id="cf-sort" type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
          </div>
          {(type === "select" || type === "multiselect") && (
            <div className="col-span-2 space-y-1">
              <Label htmlFor="cf-opts">Choices (one per line)</Label>
              <Textarea id="cf-opts" className="min-h-24" value={options} onChange={(e) => setOptions(e.target.value)} />
            </div>
          )}
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <Checkbox checked={pii} onChange={(e) => setPii(e.target.checked)} /> Personal data (kept out of logs)
          </label>
          {field && (
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <Checkbox checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Archived (hidden from the contact screen; values are kept)
            </label>
          )}
          {save.error && <p className="col-span-2 text-sm text-[var(--color-destructive)]">{save.error.message}</p>}
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !label.trim()}>
              {field ? "Save field" : "Create field"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GroupDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const save = useMutation({
    mutationFn: () => api("/api/v1/custom-field-groups", { json: { name } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["custom-field-groups"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>New field group</DialogTitle>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Park details" aria-label="Group name" required />
          {save.error && <p className="text-sm text-[var(--color-destructive)]">{save.error.message}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              Create group
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
